import { Controller } from "@hotwired/stimulus"

/**
 * The online dealer form: guards it against being submitted before it has been filled
 * in, and reports whether a chosen logo is large enough for a retina rendition before
 * the upload happens.
 *
 * The server generates a 1x and a 2x rendition and never upscales, so a master narrower
 * than the retina width silently produces a soft 2x image. Saying so at the point of
 * choosing the file is the difference between Marketing fixing it now and finding out
 * from a stakeholder later.
 *
 * The verdict is a badge, matching the one the current logo beside this field wears -
 * same colours, same icons, same two words. A reader who is told a chosen file is "Soft
 * on retina" in amber must meet exactly that on the page that comes back from the save,
 * or the two halves of the card are answering the same question differently.
 *
 * All user-facing copy arrives on data attributes rather than living here, matching the
 * WHN form controller - i18n-tasks ignores JS paths precisely because translations are
 * expected to come from the ERB. The badge colours and icons arrive the same way rather
 * than being restated here, so OnlineDealersHelper stays the only place they are chosen:
 * see OnlineDealerFormHelper::LOGO_UPLOAD_RUNGS, which is what builds the attributes below.
 *
 * Expected markup, all of it rendered by OnlineDealersHelper#online_dealer_logo_feedback_data:
 *   <form data-controller="online-dealer-form"
 *         data-action="keydown->online-dealer-form#guardImplicitSubmit">
 *     <section data-controller="image-preview">
 *       <input type="file" data-action="change->online-dealer-form#checkDimensions">
 *       <div data-online-dealer-form-target="feedback"
 *            data-retina-width="340" data-min-width="140"
 *            data-retina-ready-badge="..." data-retina-ready-text="..."
 *            data-retina-ready-class="bg-success" data-retina-ready-icon="fa-circle-check"
 *            data-ok-badge="..." ... data-too-small-badge="..." ...></div>
 *     </section>
 *   </form>
 */
export default class extends Controller {
  static targets = ["feedback", "dirty"]

  // Whether the form on screen is one the server has just refused. Rendered by the ERB
  // off `errors.any?`, because the browser cannot tell a fresh edit form from the 422
  // that replaced one - both arrive as a document with values in the fields.
  static values = { refused: Boolean }

  /**
   * Turns off implicit submission: the browser submits a form when Enter is pressed in a
   * single-line field, and this form is four sections long and filled in top to bottom.
   * Enter in the customer number - the first field, and the one people type into and then
   * pause on while the check answers - saved a stub carrying nothing else and replaced the
   * form with the list.
   *
   * Only inputs are blocked. A textarea's Enter is a newline and never submitted anyway,
   * and the submit button keeps Enter because that is the deliberate save this leaves as
   * the only way in.
   */
  connect() {
    this.dirty = false

    // A refused save is an edit that has NOT been saved, so the form that comes back
    // carries the badge and the leave warning. Turbo renders the 422 as a new document
    // and this controller reconnects on it clean, which without this would drop both
    // marks off values that only exist in the browser - the state they were meant for.
    // Set before the listeners below so a person who leaves the refused form straight
    // away is still warned.
    if (this.refusedValue) this.markDirty()

    // The browser's own warning, and the only one that survives a closed tab. It fires
    // on anything that truly leaves the document, which under Turbo Drive is fewer ways
    // out than it looks - the listener below is what routes an in-app link back into
    // this one, so Cancel warns too: a person leaving a half-typed record by the Cancel
    // button is the person this is for.
    // A save being in flight does not stand this down. Nothing is saved until the
    // response says so, and closing or reloading the tab mid-request aborts it and takes
    // the edit - the moment the warning is worth most, not least.
    this.onBeforeUnload = (event) => {
      if (!this.dirty) return

      // Both, because which one a browser honours has never been the same across two of
      // them. The text is the browser's own; nothing here can choose it.
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", this.onBeforeUnload)

    // The same warning for the way people actually leave this page. Turbo Drive is
    // started application-wide, so an ordinary link - Cancel, the sidebar, the crumb
    // back to the list - is a fetch and a swap, never an unload, and the listener above
    // does not run for any of them. Left alone, the guard would only have covered
    // closing the tab: the one exit nobody takes with a half-typed form.
    //
    // Handing the visit back to the browser rather than raising a confirm() here, so
    // there is one prompt with one wording, and it stays the browser's own - see above.
    //
    // `dirty` is the whole condition, with no exemption for a save in flight. The one
    // visit that must not be interrupted is the redirect a successful save ends in, and
    // turbo:submit-end has already cleared `dirty` by the time that visit is proposed -
    // so it passes on its own merits. An exemption would instead have covered every
    // OTHER visit in that window: Cancel, the sidebar, the crumb back to the list, each
    // abandoning the in-flight request and the edit with it, unwarned.
    this.onBeforeVisit = (event) => {
      if (!this.dirty) return

      event.preventDefault()
      window.location.href = event.detail.url
    }
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)

    // The answer, and the only thing that clears the edit. `success` is Turbo's own
    // reading of the response, so a 422 leaves the form dirty - which is the same verdict
    // the refused value above reaches when the re-rendered document reconnects this
    // controller, one arriving by state and one by markup. A network failure or an abort
    // never re-renders anything, so this is the only place that catches it: the badge
    // stays up and the leave warning comes back on a form still holding the edit.
    this.onSubmitEnd = (event) => {
      if (event.detail?.success) this.markClean()
    }
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  // Raised on the first edit and never lowered until a save. Deliberately not a diff
  // against what was loaded: a field typed into and put back is still a form somebody
  // has been working in, and telling them there is nothing to save because the letters
  // match is a worse failure than one unnecessary badge.
  //
  // Fired by input AND change, because they do not cover the same ground - input misses
  // a select being picked with the keyboard in some browsers, change misses typing until
  // blur, and a lookup filling a field dispatches input on purpose so a derived value
  // counts as an edit too. It is the same fact either way, and #markDirty is idempotent.
  markDirty() {
    if (this.dirty) return

    this.dirty = true
    if (this.hasDirtyTarget) this.dirtyTarget.hidden = false
  }

  markClean() {
    this.dirty = false
    if (this.hasDirtyTarget) this.dirtyTarget.hidden = true
  }

  guardImplicitSubmit(event) {
    if (event.key !== "Enter") return
    // Enter mid-composition commits an IME candidate; the form is never what it meant.
    if (event.isComposing) return

    const field = event.target
    if (field.tagName !== "INPUT") return
    if (field.type === "submit" || field.type === "button") return

    event.preventDefault()
  }

  checkDimensions(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return

    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      this.#render(image.naturalWidth, image.naturalHeight)
      URL.revokeObjectURL(objectUrl)
    }
    image.onerror = () => URL.revokeObjectURL(objectUrl)
    image.src = objectUrl
  }

  #render(width, height) {
    if (!this.hasFeedbackTarget) return

    const feedback = this.feedbackTarget
    const retinaWidth = Number(feedback.dataset.retinaWidth)
    const minWidth = Number(feedback.dataset.minWidth)

    // The rung names are the keys of OnlineDealerFormHelper::LOGO_UPLOAD_RUNGS. Everything
    // said about the file is looked up off the name, so the thresholds are the only
    // decision made here and the copy and colour are the server's.
    let rung
    if (width >= retinaWidth) {
      rung = "retinaReady"
    } else if (width >= minWidth) {
      rung = "ok"
    } else {
      rung = "tooSmall"
    }

    // Built as nodes with textContent rather than assigned as an HTML string: the labels
    // are translations, and one carrying an apostrophe or an ampersand in some locale
    // must not be able to reach the parser.
    feedback.className = "mt-2 small"
    feedback.replaceChildren(
      this.#badge(feedback.dataset[`${rung}Icon`],
                  feedback.dataset[`${rung}Class`],
                  feedback.dataset[`${rung}Badge`]),
      // The measurement and the explanation on their own line under the badge, muted,
      // in the same order the current logo puts them in.
      this.#detail(`${width}×${height} — ${feedback.dataset[`${rung}Text`]}`)
    )
  }

  #badge(icon, className, label) {
    const badge = document.createElement("span")
    badge.className = `badge ${className}`

    const glyph = document.createElement("i")
    glyph.className = `fa-solid ${icon} me-1`
    glyph.setAttribute("aria-hidden", "true")

    badge.append(glyph, document.createTextNode(label))
    return badge
  }

  #detail(text) {
    const detail = document.createElement("span")
    detail.className = "d-block text-muted mt-1"
    detail.textContent = text
    return detail
  }
}
